import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Chip,
  alpha,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Rule as RuleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  createConsentReason,
  deleteConsentReason,
  getConsentReasons,
  updateConsentReason,
} from "../../../api/services/Assessment/ConsentCapacity";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import CustomInput from "../../CustomForm/CustomInput";

const FormDialog = ({ open, handleClose, onSubmit, editingItem, isLoading }) => {
  const [name, setName] = useState(editingItem?.name || "");

  useEffect(() => {
    setName(editingItem?.name || "");
  }, [editingItem]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSubmit({ name });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha("#2469F0", 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RuleIcon sx={{ color: "#2469F0", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              {editingItem ? "Edit Consent Reason" : "Add Consent Reason"}
            </Typography>
            <Typography
              variant="caption"
              color="#64748b"
              sx={{ fontSize: "0.7rem" }}
            >
              {editingItem
                ? "Update the reason details"
                : "Create a new consent reason"}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: "#94A3B8", p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ pt: 3, px: 2.5 }}>
        <CustomInput
          label="Consent Reason *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="medium"
          fullWidth
          required
          autoFocus
        />
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions sx={{ borderTop: "1px solid #E2E8F0", p: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "6px",
            borderColor: "#D0D5DD",
            color: "#344054",
            px: 3,
            py: 0.75,
            "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon sx={{ fontSize: 18 }} />
            )
          }
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "6px",
            bgcolor: "#2469F0",
            px: 3,
            py: 0.75,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
            },
            "&.Mui-disabled": { bgcolor: "#F1F5F9", color: "#94A3B8" },
          }}
        >
          {editingItem ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConsentReasons = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading } = useQuery("consentReasons", getConsentReasons);

  const mutation = useMutation(
    (item) =>
      editingItem
        ? updateConsentReason(editingItem.id, item)
        : createConsentReason(item),
    {
      onSuccess: () => {
        enqueueSnackbar("Saved successfully", { variant: "success" });
        queryClient.invalidateQueries("consentReasons");
        setDialogOpen(false);
        setEditingItem(null);
      },
      onError: () => enqueueSnackbar("Failed to save", { variant: "error" }),
    }
  );

  const handleConfirmDelete = () => {
    if (!deleteId) return;
    setLoadingDelete(true);
    deleteConsentReason(deleteId)
      .then(() => {
        enqueueSnackbar("Deleted successfully", { variant: "success" });
        queryClient.invalidateQueries("consentReasons");
      })
      .catch(() => enqueueSnackbar("Failed to delete", { variant: "error" }))
      .finally(() => {
        setLoadingDelete(false);
        setConfirmOpen(false);
        setDeleteId(null);
      });
  };

  const handleSubmit = (item) => mutation.mutate(item);

  const totalCount = data?.length || 0;

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        sx={{
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RuleIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                Consent Reasons
              </Typography>
              <Typography
                variant="caption"
                color="#64748b"
                sx={{ fontSize: "0.7rem" }}
              >
                Manage reasons for client consent
              </Typography>
            </Box>
            <Chip
              label={`${totalCount} item${totalCount !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
              }}
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setDialogOpen(true)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            Add Consent Reason
          </Button>
        </Box>

        {/* CONTENT */}
        <Box sx={{ p: 2.5 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : data?.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  bgcolor: alpha("#94A3B8", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <RuleIcon sx={{ fontSize: 24, color: "#94A3B8" }} />
              </Box>
              <Typography
                variant="body2"
                color="#475467"
                fontWeight={500}
                sx={{ fontSize: "0.85rem" }}
              >
                No consent reasons found
              </Typography>
              <Typography
                variant="caption"
                color="#94A3B8"
                sx={{ fontSize: "0.7rem" }}
              >
                Click "Add Consent Reason" to create one
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {data?.map((item) => (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: "8px",
                    borderColor: "#E2E8F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: "1 1 calc(33.33% - 12px)",
                    minWidth: "220px",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: alpha("#2469F0", 0.4),
                      boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.08)",
                      "& .action-icons": { opacity: 1 },
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="#1A1A2E"
                    sx={{
                      fontSize: "0.82rem",
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      mr: 1,
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Box
                    className="action-icons"
                    sx={{
                      display: "flex",
                      gap: 0.25,
                      opacity: { xs: 1, md: 0.7 },
                      transition: "opacity 0.15s ease",
                    }}
                  >
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingItem(item);
                          setDialogOpen(true);
                        }}
                        sx={{
                          color: "#667085",
                          p: 0.5,
                          "&:hover": {
                            color: "#F59E0B",
                            bgcolor: alpha("#F59E0B", 0.06),
                          },
                        }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteId(item.id);
                          setConfirmOpen(true);
                        }}
                        sx={{
                          color: "#667085",
                          p: 0.5,
                          "&:hover": {
                            color: "#EF4444",
                            bgcolor: alpha("#EF4444", 0.06),
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      <FormDialog
        open={dialogOpen}
        handleClose={() => {
          setDialogOpen(false);
          setEditingItem(null);
        }}
        onSubmit={handleSubmit}
        editingItem={editingItem}
        isLoading={mutation.isLoading}
      />

      <ConfirmModal
        show={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Delete Consent Reason"
        body="Are you sure you want to delete this consent reason?"
        isDelete
        isLoading={loadingDelete}
      />
    </Box>
  );
};

export default ConsentReasons;